import type {
  TReferenceDesignCatalogItem,
  TReferenceWellboreCatalogItem,
  TWellChildrenCatalog,
} from '@repo/reference-wells';

import type { IDirectoriesService } from 'src/packages/directories/IDirectoriesService';
import type { TDictObject } from 'src/packages/directories/types';

type TWellboreTypeLookup = {
  typeId: number;
  typeInstanceId: number;
  typeInstanceName: string;
};

function normalizeSlug(value: string | null | undefined): string {
  return (value ?? '').toString().trim().toLowerCase();
}

function buildWellboreTypeLookup(
  types: TDictObject[] | null,
  instances: TDictObject[] | null,
): Map<string, TWellboreTypeLookup> {
  const lookup = new Map<string, TWellboreTypeLookup>();

  if (!types?.length || !instances?.length) {
    return lookup;
  }

  const typesById = new Map<number, TDictObject>();

  for (const type of types) {
    const id = typeof type.id === 'number' ? type.id : Number(type.id);

    if (!Number.isFinite(id)) {
      continue;
    }

    typesById.set(id, type);
  }

  for (const instance of instances) {
    const typeIdRaw = instance.data.typeId;
    const typeId = typeof typeIdRaw === 'number' ? typeIdRaw : Number(typeIdRaw);
    const indexRaw = instance.data.index;
    const index = typeof indexRaw === 'number' ? indexRaw : Number(indexRaw);
    const instanceId = typeof instance.id === 'number' ? instance.id : Number(instance.id);
    const name = instance.data.name;

    if (!Number.isFinite(typeId) || !Number.isFinite(index) || !Number.isFinite(instanceId) || name == null) {
      continue;
    }

    const type = typesById.get(typeId);

    if (!type) {
      continue;
    }

    const slug = normalizeSlug(type.data.slug as string | undefined);

    if (!slug) {
      continue;
    }

    lookup.set(`${slug}:${index}`, {
      typeId,
      typeInstanceId: instanceId,
      typeInstanceName: String(name),
    });
  }

  return lookup;
}

function enrichWellbore(
  wellbore: TReferenceWellboreCatalogItem,
  lookup: Map<string, TWellboreTypeLookup>,
): TReferenceWellboreCatalogItem {
  const key = `${normalizeSlug(wellbore.typeSlug)}:${wellbore.typeIndex}`;
  const match = lookup.get(key);

  if (!match) {
    return wellbore;
  }

  return {
    ...wellbore,
    typeId: match.typeId,
    typeInstanceId: match.typeInstanceId,
    typeInstanceName: match.typeInstanceName,
  };
}

function enrichDesign(
  design: TReferenceDesignCatalogItem,
  lookup: Map<string, TWellboreTypeLookup>,
): TReferenceDesignCatalogItem {
  return {
    ...design,
    wellbores: design.wellbores.map((wellbore) => enrichWellbore(wellbore, lookup)),
  };
}

export function enrichReferenceWellChildrenCatalog(
  children: TWellChildrenCatalog,
  lookup: Map<string, TWellboreTypeLookup>,
): TWellChildrenCatalog {
  return {
    factWellbores: children.factWellbores.map((wellbore) => enrichWellbore(wellbore, lookup)),
    planDesigns: children.planDesigns.map((design) => enrichDesign(design, lookup)),
  };
}

export async function loadWellboreTypeLookup(
  directories: IDirectoriesService,
): Promise<Map<string, TWellboreTypeLookup>> {
  await directories.loadObjects(['Common_WellboreType', 'Common_WellboreTypeInstance']);

  return buildWellboreTypeLookup(
    directories.getObject('Common_WellboreType'),
    directories.getObject('Common_WellboreTypeInstance'),
  );
}

export function enrichReferenceWellChildrenByWellId(
  childrenByWellId: Map<number, TWellChildrenCatalog>,
  lookup: Map<string, TWellboreTypeLookup>,
): Map<number, TWellChildrenCatalog> {
  const next = new Map<number, TWellChildrenCatalog>();

  for (const [wellId, children] of childrenByWellId) {
    next.set(wellId, enrichReferenceWellChildrenCatalog(children, lookup));
  }

  return next;
}

/** Build typeInstanceId → name map from the same directory cache used for enrichment. */
export function buildWellboreTypeInstanceNameMap(instances: TDictObject[] | null): Map<number, string> {
  const map = new Map<number, string>();

  if (!instances) {
    return map;
  }

  for (const instance of instances) {
    const id = typeof instance.id === 'number' ? instance.id : Number(instance.id);
    const name = instance.data.name;

    if (!Number.isFinite(id) || name == null) {
      continue;
    }

    map.set(id, String(name));
  }

  return map;
}
